import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../../utils/apolloClient';
import { environmentURLs } from '../../../components/common/CaptureEnvironment';
import { DMS_ACCOUNT_TYPES_MAPPING } from '../../../components/stepper/stepperBody/stepperBodyComponents/DMSSelectionComponents/utils/constant';
import { API_V2_PREFIX } from '../../../constants/constants';
import {
  persistOptimisticPending,
  restoreOptimisticPending,
} from './optimisticPendingStorage';

export const initialState = {
  subscriptions: [],
  subscriptionsByStatus: {},
  fetchingSubscriptions: true,
  errorFetchingSubscriptions: false,
  sendingSubscribeRequest: false,
  subscribeRequestSent: false,
  sendingUnsubscribeRequest: false,
  unSubscribeRequestSent: false,
  errorSendingSubscribeRequest: false,
  errorSendingUnsubscribeRequest: false,
  dmsSupportedApis: {},
  removeLocationsApiName: '',
  fetchingSystemInfo: false,
  errorfetchingSystemInfo: false,
  subscribeRequest: {},
  unsubscribeRequest: {},
  dmsByEntity: {},
  fetchingDMSdata: false,
  errorFetchingDMSdata: false,
};

const formatSubscriptionsData = (data) => {
  const groupByStatus = (items) => {
    return items.reduce((acc, item) => {
      const status = item.status;
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(item);
      return acc;
    }, {});
  };
  return groupByStatus(data);
};

const getSubscriptionEntityId = (item) =>
  item?.orgId || item?.entityId || item?.dataOwnerOrgId;

const getApiScopeKey = (apiId, apiType) => `${apiId || ''}::${apiType || ''}`;

const getRequestedStatus = (item) =>
  (item?.requested_status || item?.requestedStatus || '').toLowerCase();

const isPendingLikeStatus = (status) => {
  const normalizedStatus = (status || '').toLowerCase();
  return ['pending', 'requested', 'in review', 'in_review'].includes(
    normalizedStatus
  );
};

const isInactiveLikeStatus = (status) => {
  const normalizedStatus = (status || '').toLowerCase();
  return ['inactive', 'disabled', 'unsubscribed'].includes(normalizedStatus);
};

const mergeWithOptimisticPendingSubscriptions = (
  currentSubscriptions = [],
  fetchedSubscriptions = [],
  apiScopeKey = ''
) => {
  const isFetchingSpecificApi = Boolean(apiScopeKey && apiScopeKey !== '::');
  const currentScopedByEntityId = new Map(
    (Array.isArray(currentSubscriptions) ? currentSubscriptions : [])
      .filter((item) => item?.optimisticApiScopeKey === apiScopeKey)
      .map((item) => [getSubscriptionEntityId(item), item])
      .filter(([entityId]) => Boolean(entityId))
  );

  const optimisticPendingByEntityId = new Map(
    (Array.isArray(currentSubscriptions) ? currentSubscriptions : [])
      .filter((item) => {
        const isOptimisticPending =
          getRequestedStatus(item) === 'subscribe' ||
          isPendingLikeStatus(item?.status);

        if (isFetchingSpecificApi) {
          if (!isOptimisticPending) return false;
          return item?.optimisticApiScopeKey === apiScopeKey;
        }

        return isOptimisticPending || !!item?.optimisticApiScopeKey;
      })
      .map((item) => [getSubscriptionEntityId(item), item])
      .filter(([entityId]) => Boolean(entityId))
  );

  const fetchedEntityIds = new Set(
    (Array.isArray(fetchedSubscriptions) ? fetchedSubscriptions : [])
      .map((item) => getSubscriptionEntityId(item))
      .filter(Boolean)
  );

  const subscriptionsFromOtherApis = new Map(
    isFetchingSpecificApi
      ? (Array.isArray(currentSubscriptions) ? currentSubscriptions : [])
          .filter((item) => {
            const entityId = getSubscriptionEntityId(item);
            if (!entityId) return false;
            if (item?.optimisticApiScopeKey === apiScopeKey) return false;

            if (
              fetchedEntityIds.has(entityId) &&
              !currentScopedByEntityId.has(entityId)
            ) {
              return false;
            }

            return (
              !!item?.optimisticApiScopeKey &&
              item.optimisticApiScopeKey !== apiScopeKey
            );
          })
          .map((item) => {
            const entityId = getSubscriptionEntityId(item);
            return [
              `${entityId}::${item?.optimisticApiScopeKey || '__unscoped__'}`,
              item,
            ];
          })
          .filter(([subscriptionKey]) => !subscriptionKey.startsWith('::'))
      : new Map()
  );

  const mergedByEntityId = new Map(
    (Array.isArray(fetchedSubscriptions) ? fetchedSubscriptions : [])
      .map((item) => [getSubscriptionEntityId(item), item])
      .filter(([entityId]) => Boolean(entityId))
  );

  optimisticPendingByEntityId.forEach((optimisticItem, entityId) => {
    const fetchedItem = mergedByEntityId.get(entityId);

    if (!fetchedItem) {
      mergedByEntityId.set(entityId, optimisticItem);
      return;
    }

    const fetchedRequestedStatus = getRequestedStatus(fetchedItem);
    if (
      fetchedRequestedStatus !== 'subscribe' &&
      isInactiveLikeStatus(fetchedItem?.status)
    ) {
      mergedByEntityId.set(entityId, {
        ...fetchedItem,
        ...optimisticItem,
        status: 'pending',
        requested_status: 'subscribe',
        optimisticApiScopeKey: optimisticItem.optimisticApiScopeKey,
      });
    }
  });

  const result = Array.from(mergedByEntityId.values());

  subscriptionsFromOtherApis.forEach((item) => {
    const entityId = getSubscriptionEntityId(item);

    if (!entityId) {
      return;
    }

    const alreadyExists = result.some(
      (resultItem) =>
        getSubscriptionEntityId(resultItem) === entityId &&
        resultItem?.optimisticApiScopeKey === item?.optimisticApiScopeKey
    );

    if (!alreadyExists) {
      result.push(item);
    }
  });

  return result;
};

export const getSubscriptionsData = createAsyncThunk(
  'getSubscriptionsData',
  async ({ solutionId, apiId, apiType, token }) => {
    let queryParams = `solutionId=${solutionId}`;
    if (apiId && apiType) {
      queryParams = queryParams + `&apiId=${apiId}&apiType=${apiType}`;
    }
    let response = await client.authorizeRequest({
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/subscriptions?${queryParams}`,
    });
    return {
      data: response.data,
    };
  }
);

export const sendSubscribeRequest = createAsyncThunk(
  'sendSubscribeRequest',
  async ({ payload, token }) => {
    let response = await client.authorizeRequest({
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/subscriptions`,
      data: payload,
    });
    return {
      data: response.data,
    };
  }
);

export const sendUnsubscribeRequest = createAsyncThunk(
  'sendUnsubscribeRequest',
  async ({ payload, token }) => {
    let response = await client.authorizeRequest({
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      url: `${environmentURLs.REACT_APP_DEALER_API_BUYER_SERVICE_URL}/subscriptions/deactivate`,
      data: payload,
    });
    return {
      data: response.data,
    };
  }
);

export const getApisSystemInfo = createAsyncThunk(
  'getApisSystemInfo',
  async ({ apis, token }) => {
    const response = await client.authorizeRequest({
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      url: `${environmentURLs.REACT_APP_MP_DP_METADATA_URL}/v2/systemInfo`,
      data: {
        apis,
      },
    });
    return {
      ...response.data,
      apis,
    };
  }
);

export const getDMSDetailsForStore = createAsyncThunk(
  'getDMSDetailsForStore',
  async ({ token, entityId, cmfNumber, storeId, apiDMS }) => {
    let response = await client.authorizeRequest({
      method: 'GET',
      url: `${environmentURLs.REACT_APP_MP_DP_METADATA_URL}/v1/connectCDK/${storeId}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return {
      data: response.data,
      cmfNumber,
      storeId,
      entityId,
      apiDMS,
    };
  }
);

export const daabSubscriptionsSlice = createSlice({
  name: 'daabSubscriptionsSlice',
  initialState,
  reducers: {
    setSubscribeRequest: (state, action) => {
      state.subscribeRequest = action.payload;
    },
    setUnsubscribeRequest: (state, action) => {
      state.unsubscribeRequest = action.payload;
    },
    resetDaabSubscriptionsState: (state, action) => {
      state.subscribeRequest = {};
      state.unsubscribeRequest = {};
      state.fetchingSubscriptions = false;
      state.errorFetchingSubscriptions = false;
      state.sendingSubscribeRequest = false;
      state.subscribeRequestSent = false;
      state.sendingUnsubscribeRequest = false;
      state.errorSendingSubscribeRequest = false;
      state.errorSendingUnsubscribeRequest = false;
      state.dmsByEntity = {};
      state.fetchingDMSdata = false;
      state.errorFetchingDMSdata = false;
    },
    clearSubscriptions: (state) => {
      state.subscriptionsByStatus = {};
      state.fetchingSubscriptions = true;
    },
    seedPendingSubscriptions: (state, action) => {
      const pendingToSeed = Array.isArray(action.payload)
        ? action.payload
        : [];
      if (pendingToSeed.length > 0 && Array.isArray(state.subscriptions)) {
        const seedByKey = new Map(
          pendingToSeed
            .map((sub) => {
              const entityId = getSubscriptionEntityId(sub);
              if (!entityId) return null;
              const scope = sub?.optimisticApiScopeKey || '__unscoped__';
              return [`${entityId}::${scope}`, sub];
            })
            .filter(Boolean)
        );
        const existingByKey = new Map(
          state.subscriptions
            .map((sub) => {
              const entityId = getSubscriptionEntityId(sub);
              if (!entityId) return null;
              const scope = sub?.optimisticApiScopeKey || '__unscoped__';
              return [`${entityId}::${scope}`, sub];
            })
            .filter(Boolean)
        );
        seedByKey.forEach((sub, key) => existingByKey.set(key, sub));
        state.subscriptions = Array.from(existingByKey.values());
        state.subscriptionsByStatus = formatSubscriptionsData(
          state.subscriptions
        );
      }
    },
    setRemoveLocationsApiName: (state, action) => {
      state.removeLocationsApiName = action.payload;
    },
    resetunSubscribeRequestSentFlag: (state) => {
      state.unSubscribeRequestSent = false;
      state.errorSendingUnsubscribeRequest = false;
    },
    resetSubscribeRequestSentFlag: (state) => {
      state.subscribeRequestSent = false;
      state.errorSendingSubscribeRequest = false;
    },
  },
  extraReducers: {
    [getSubscriptionsData.fulfilled]: (state, action) => {
      const apiScopeKey = getApiScopeKey(
        action?.meta?.arg?.apiId,
        action?.meta?.arg?.apiType
      );

      const seededSubscriptions =
        Array.isArray(state.subscriptions) && state.subscriptions.length > 0
        ? state.subscriptions
        : restoreOptimisticPending();

      const normalizedFetchedSubscriptions =
        apiScopeKey && apiScopeKey !== '::'
          ? (Array.isArray(action.payload.data) ? action.payload.data : []).map(
              (item) => ({
                ...item,
                optimisticApiScopeKey: apiScopeKey,
              })
            )
          : action.payload.data;

      const mergedSubscriptions = mergeWithOptimisticPendingSubscriptions(
        seededSubscriptions,
        normalizedFetchedSubscriptions,
        apiScopeKey
      );

      state.fetchingSubscriptions = false;
      state.errorFetchingSubscriptions = false;
      state.subscriptions = mergedSubscriptions;
      state.subscriptionsByStatus = formatSubscriptionsData(mergedSubscriptions);
      persistOptimisticPending(mergedSubscriptions);
    },
    [getSubscriptionsData.pending]: (state) => {
      state.fetchingSubscriptions = true;
    },
    [getSubscriptionsData.rejected]: (state) => {
      state.fetchingSubscriptions = false;
      state.errorFetchingSubscriptions = true;
    },
    [getApisSystemInfo.fulfilled]: (state, action) => {
      const { apiDetails } = action.payload;
      let dmsAccountsSupportByAPI = {};
      for (const apiId in apiDetails) {
        const dmsData = apiDetails[apiId];
        const cleanApiId = apiId.replace(API_V2_PREFIX, '');
        if (Array.isArray(dmsData) && dmsData.length) {
          // format: { (API-ID): [ ["Service", "Parts"] ] }
          if (Array.isArray(dmsData[0])) {
            dmsAccountsSupportByAPI[cleanApiId] = dmsData[0];
          } // format: { (API-ID): ["Service"] }
          else {
            dmsAccountsSupportByAPI[cleanApiId] = dmsData;
          }
        }
      }
      state.fetchingSystemInfo = false;
      state.errorfetchingSystemInfo = false;
      state.dmsSupportedApis = dmsAccountsSupportByAPI;
    },
    [getApisSystemInfo.pending]: (state) => {
      state.fetchingSystemInfo = true;
      state.errorfetchingSystemInfo = false;
    },
    [getApisSystemInfo.rejected]: (state) => {
      state.fetchingSystemInfo = false;
      state.errorfetchingSystemInfo = true;
    },
    [sendSubscribeRequest.fulfilled]: (state, action) => {
      state.sendingSubscribeRequest = false;
      state.subscribeRequestSent = true;
      state.errorSendingSubscribeRequest = false;

      const requestedEntities = action?.meta?.arg?.payload?.entityIdList;
      const apiScopeKey = getApiScopeKey(
        action?.meta?.arg?.payload?.api?.id,
        action?.meta?.arg?.payload?.api?.type
      );

      if (Array.isArray(requestedEntities) && requestedEntities.length) {
        const mergedSubscriptions = Array.isArray(state.subscriptions)
          ? [...state.subscriptions]
          : [];

        requestedEntities.forEach((requestedEntity) => {
          const entityId = getSubscriptionEntityId(requestedEntity);

          if (!entityId) {
            return;
          }

          const existingIndex = mergedSubscriptions.findIndex((sub) => {
            const sameEntity = getSubscriptionEntityId(sub) === entityId;
            const normalizedSubScope =
              sub?.optimisticApiScopeKey || apiScopeKey;
            const sameApiScope = normalizedSubScope === apiScopeKey;
            return sameEntity && sameApiScope;
          });

          const optimisticPendingSubscription = {
            ...requestedEntity,
            orgId: requestedEntity?.orgId || entityId,
            entityId: requestedEntity?.entityId || entityId,
            status: 'pending',
            requested_status: 'subscribe',
            optimisticApiScopeKey: apiScopeKey,
          };

          if (existingIndex >= 0) {
            mergedSubscriptions[existingIndex] = {
              ...mergedSubscriptions[existingIndex],
              ...optimisticPendingSubscription,
            };
          } else {
            mergedSubscriptions.push(optimisticPendingSubscription);
          }
        });

        state.subscriptions = mergedSubscriptions;
        state.subscriptionsByStatus = formatSubscriptionsData(mergedSubscriptions);
        persistOptimisticPending(mergedSubscriptions);
      }
    },
    [sendSubscribeRequest.pending]: (state) => {
      state.sendingSubscribeRequest = true;
      state.subscribeRequestSent = false;
      state.errorSendingSubscribeRequest = false;
    },
    [sendSubscribeRequest.rejected]: (state) => {
      state.sendingSubscribeRequest = false;
      state.subscribeRequestSent = false;
      state.errorSendingSubscribeRequest = true;
    },
    [sendUnsubscribeRequest.fulfilled]: (state, action) => {
      state.sendingUnsubscribeRequest = false;
      state.unSubscribeRequestSent = true;
      state.errorSendingUnsubscribeRequest = false;
    },
    [sendUnsubscribeRequest.pending]: (state) => {
      state.sendingUnsubscribeRequest = true;
      state.unSubscribeRequestSent = false;
      state.errorSendingUnsubscribeRequest = false;
    },
    [sendUnsubscribeRequest.rejected]: (state) => {
      state.sendingUnsubscribeRequest = false;
      state.unSubscribeRequestSent = false;
      state.errorSendingUnsubscribeRequest = true;
    },
    [getDMSDetailsForStore.fulfilled]: (state, action) => {
      const { data, cmfNumber, entityId, apiDMS = [] } = action?.payload;
      const departments = data?.departments || [];
      const entityDMS = departments?.map((dept) => ({
        id: dept.id,
        name: dept.name,
      }));
      const entitySubReq = state.subscribeRequest[entityId];
      const departmentsByType = departments.reduce((acc, dept) => {
        const typeName = DMS_ACCOUNT_TYPES_MAPPING[dept.type] || 'Other';

        if (!acc[typeName]) {
          acc[typeName] = [];
        }

        acc[typeName].push({
          id: dept.id,
          name: dept.name,
          dmsType: typeName,
        });

        return acc;
      }, {});

      let initialDmsSelection = [];
      // if only one dept, map it to all dms in API
      // else select any one for respective api dms types
      if (entityDMS.length) {
        if (entityDMS.length === 1) {
          initialDmsSelection = apiDMS?.map((dms) => {
            return { ...entityDMS[0], dmsType: dms };
          });
        } else {
          initialDmsSelection = apiDMS
            ?.map((dms) => {
              if (departmentsByType[dms]) return departmentsByType[dms][0];
            })
            .filter(Boolean);
        }
      }

      const dmsOptionsByType = {};
      apiDMS?.forEach((dmsType) => {
        const selected = initialDmsSelection.find(
          (sel) => sel.dmsType === dmsType
        );
        const rest = entityDMS?.filter((dept) => dept.id !== selected?.id);
        dmsOptionsByType[dmsType] = selected
          ? [{ id: selected.id, name: selected.name }, ...rest]
          : [...rest];
      });

      state.dmsByEntity = {
        ...state.dmsByEntity,
        [entityId]: {
          cmfNumber,
          entityId,
          departments: dmsOptionsByType,
        },
      };
      state.subscribeRequest = {
        ...state.subscribeRequest,
        [entityId]: { ...entitySubReq, dmsConfig: initialDmsSelection },
      };
      state.fetchingDMSdata = false;
      state.errorFetchingDMSdata = false;
    },
    [getDMSDetailsForStore.pending]: (state, action) => {
      const { entityId } = action?.meta?.arg;
      state.dmsByEntity = {
        ...state.dmsByEntity,
        [entityId]: { status: 'LOADING' },
      };
      state.fetchingDMSdata = true;
      state.errorFetchingDMSdata = false;
    },
    [getDMSDetailsForStore.rejected]: (state, action) => {
      const { entityId } = action?.meta?.arg;
      state.dmsByEntity = {
        ...state.dmsByEntity,
        [entityId]: { status: 'ERROR' },
      };
      state.fetchingDMSdata = false;
      state.errorFetchingDMSdata = true;
    },
  },
});

export const {
  setSubscribeRequest,
  setUnsubscribeRequest,
  resetDaabSubscriptionsState,
  clearSubscriptions,
  seedPendingSubscriptions,
  setRemoveLocationsApiName,
  resetunSubscribeRequestSentFlag,
  resetSubscribeRequestSentFlag,
} = daabSubscriptionsSlice.actions;

export default daabSubscriptionsSlice.reducer;
